import { TourHelpButton } from '@/components/tour-help-button';
import { WORKSPACE_ROUTE_PATTERN, workspaceUrl } from './workspace-routes';
import { useCanvasStore } from './canvas-store';
import { resolveProjectNavigation } from './project-navigation';
import { useActiveProjectMessages, useActiveProjectActiveView, useActiveProjectId } from './store-selectors';
import type { CanvasView } from './canvas-types';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  Tag,
  BarChart2,
  Megaphone,
  Loader2,
  Search,
  PlayCircle,
  Grid,
  Plus,
  Folder,
  FolderOpen,
  Clapperboard,
  MessageCircle,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Star,
  ExternalLink,
  FolderInput,
  Trash2,
  PanelLeftClose,
  PenSquare,
  BarChart3,
  LayoutGrid,
  ListTodo,
  MessageSquare,
  Grid2X2,
  Settings,
  Pin,
  PinOff,
  X,
  Database,
  Shield,
 Compass } from 'lucide-react';
import { SettingsModal } from '../../components/settings-modal';

import LogoAnimation from '../../components/LogoAnimation';
import { CreateProjectModal } from './canvas-create-project-modal';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { useAuthUser } from '@/App';
import { APP_VERSION } from '@/lib/version';

// v6.62 — tasks are brand-scoped now (Dan's call): the standalone "All Tasks"
// list (expanded section, collapsed bubble, "All chats" link) is hidden, not
// deleted — flip this back on if it's ever needed again.
const SHOW_ALL_TASKS = false;

const TOP_NAV = [
  // v3.92 — reordered + renamed per Dan: Discover (was 'Ad library') sits right
  // under New Task; Heartbeat (was 'Heartbeat AI') moved to the bottom under
  // Analytics.
  { label: 'Discover', href: null, icon: LayoutGrid, view: 'ad-library' as const },
  { label: 'Assets', href: null, icon: FolderOpen, view: 'assets' as const },
  // FUTURE-EDITOR (v6.66): Projects (the OpenCut editor) is PARKED per Dan —
  // restore this entry when the editor returns.
  // { label: 'Projects', href: '/api/video-editor/projects/', icon: Clapperboard },
  { label: 'Brands', href: null, icon: Tag, view: 'brands' as const },
  { label: 'Analytics', href: null, icon: BarChart2, view: 'analytics' as const },
  { label: 'Heartbeat', href: null, icon: Activity, view: 'heartbeat' as const },
  // Ads Manager intentionally NOT here — it's not user-ready, so it lives in
  // the admin-only Dev Tools section below until launch.
];

type QuickToolWithView = { label: string; href: null; icon: React.ComponentType<{ style?: React.CSSProperties; className?: string }>; view: CanvasView };
type QuickToolWithHref = { label: string; href: string; icon: React.ComponentType<{ style?: React.CSSProperties; className?: string }>; view?: never };
type QuickTool = QuickToolWithView | QuickToolWithHref;

const DEV_TOOLS: QuickTool[] = [
  { label: 'Admin', href: '/admin', icon: Shield },
  { label: 'Ads Manager', href: null, icon: Megaphone, view: 'ads-manager' as const },
  { label: 'Mock Data', href: '/mock-data', icon: Database },
];

const BOTTOM_ICONS = [
  { label: 'Settings', icon: Settings },
];

interface ApiProject {
  id: number;
  name: string;
  color?: string;
  // The /api/canvas/projects endpoint returns snake_case (brand_id, ad_account_id,
  // parent_id) — accept both shapes here so the sidebar works regardless of
  // the response format. Helper readers below normalize.
  brandId?: number | null;
  brand_id?: number | null;
  adAccountId?: number | null;
  ad_account_id?: number | null;
  parentId?: number | null;
  parent_id?: number | null;
  isPinned?: boolean;
  is_pinned?: boolean;
  isFolder?: boolean;
  is_folder?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

function SectionHeader({
  label,
  action,
  collapsed,
  onToggle,
}: {
  label: string;
  action?: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div
      className="flex items-center justify-between px-3 mb-1"
      style={{ minHeight: 28, cursor: onToggle ? 'pointer' : undefined }}
      onClick={onToggle}
    >
      <div className="flex items-center gap-1">
        {onToggle !== undefined && (
          <ChevronRight
            style={{
              width: 11,
              height: 11,
              color: '#FFFFFF',
              flexShrink: 0,
              transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              transition: 'transform 200ms ease',
            }}
          />
        )}
        <span style={{ fontSize: 11, fontWeight: 500, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </div>
      {action && (
        <div onClick={(e) => e.stopPropagation()}>
          {action}
        </div>
      )}
    </div>
  );
}

function NavItem({
  icon: Icon,
  label,
  onClick,
  active = false,
  isCreating = false,
  testId,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  active?: boolean;
  isCreating?: boolean;
  /** v7.44 — stable anchor for the first-run tour (label text is not one). */
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-2.5 text-left transition-colors"
      style={{
        padding: '7px 12px',
        borderRadius: '8px',
        fontSize: '13.5px',
        color: '#FFFFFF',
        background: active ? '#2A2A2A' : 'transparent',
        fontWeight: active ? 500 : 400,
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      {isCreating ? (
        <Loader2 className="flex-shrink-0 animate-spin" style={{ width: 16, height: 16, color: '#FFFFFF' }} />
      ) : (
        <Icon className="flex-shrink-0" style={{ width: 16, height: 16, color: '#FFFFFF' }} />
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}

function ProjectRow({
  project,
  isOpen,
  isActiveFolder,
  hasFolderActiveTask,
  activeTaskId,
  onClickFolder,
  onClickTask,
  onAddTask,
  onRename,
  onDelete,
  onTogglePin,
  isRenaming,
  renameValue,
  onRenameChange,
  onRenameSave,
  onRenameCancel,
  renameInputRef,
  onShowAllChats,
  analyzing = false,
}: {
  project: ApiProject;
  /** v7.50 — the folder's brand is still being onboarded: the row pulses
   *  and can't be opened until the scan finishes (Dan: "fading in and out
   *  like in progress, so you can't use it yet"). */
  analyzing?: boolean;
  isOpen: boolean;
  isActiveFolder: boolean;
  hasFolderActiveTask: boolean;
  activeTaskId: number | null;
  onClickFolder: () => void;
  onClickTask: (task: ApiProject) => void;
  onAddTask: () => void;
  onRename: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  isRenaming: boolean;
  renameValue: string;
  onRenameChange: (val: string) => void;
  onRenameSave: () => void;
  onRenameCancel: () => void;
  renameInputRef: React.RefObject<HTMLInputElement>;
  onShowAllChats?: () => void;
}) {
  const { data: tasks = [] } = useQuery<ApiProject[]>({
    queryKey: ['/api/canvas/projects', project.id, 'tasks'],
    queryFn: async () => {
      const resp = await fetch(`/api/canvas/projects/${project.id}/tasks`);
      if (!resp.ok) return [];
      return resp.json();
    },
    enabled: isOpen,
    staleTime: 10000,
  });
  const FolderIcon = isOpen ? FolderOpen : Folder;
  const folderBg = isActiveFolder ? '#1A1A1A' : hasFolderActiveTask ? '#1A1A1A' : 'transparent';
  const folderColor = '#FFFFFF';
  const folderWeight = isActiveFolder ? 500 : 400;
  const isPinned = !!(project.isPinned || project.is_pinned);

  return (
    <div>
      <div
        data-testid={`brand-folder-${project.id}`}
        data-analyzing={analyzing ? 'true' : undefined}
        className="w-full flex items-center gap-2 text-left group transition-colors"
        style={{
          padding: '6px 8px 6px 12px',
          borderRadius: '8px',
          fontSize: '13px',
          color: folderColor,
          background: analyzing ? '#141414' : folderBg,
          fontWeight: folderWeight,
          cursor: analyzing ? 'progress' : isRenaming ? 'default' : 'pointer',
          opacity: analyzing ? 0.7 : 1,
          pointerEvents: analyzing ? 'none' : undefined,
          // v7.51 — the skeleton sweep overlay is absolutely positioned
          // inside the row; clip it to the row's radius.
          position: analyzing ? 'relative' : undefined,
          overflow: analyzing ? 'hidden' : undefined,
        }}
        title={analyzing ? 'Analyzing brand — ready in a minute' : undefined}
        onClick={() => { if (!isRenaming && !analyzing) onClickFolder(); }}
        onMouseEnter={(e) => { if (!isActiveFolder && !isRenaming) (e.currentTarget as HTMLDivElement).style.background = '#1A1A1A'; }}
        onMouseLeave={(e) => { if (!isActiveFolder) (e.currentTarget as HTMLDivElement).style.background = folderBg; }}
      >
        {analyzing && <span aria-hidden className="sidebar-folder-shimmer" data-testid={`brand-folder-shimmer-${project.id}`} />}
        <FolderIcon style={{ width: 14, height: 14, color: '#FFFFFF', flexShrink: 0 }} />
        {isRenaming ? (
          <input
            ref={renameInputRef}
            data-testid={`folder-rename-input-${project.id}`}
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onRenameSave(); }
              if (e.key === 'Escape') { e.preventDefault(); onRenameCancel(); }
            }}
            onBlur={() => onRenameCancel()}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0"
            style={{
              background: 'white',
              border: '1px solid #E5E2DC',
              borderRadius: 4,
              padding: '1px 4px',
              fontSize: 13,
              color: '#1A1A1A',
              outline: 'none',
            }}
          />
        ) : (
          <span className="truncate flex-1" style={{ fontSize: 13 }}>{project.name}</span>
        )}
        {!isRenaming && (
          <>
            {isPinned && (
              <Pin style={{ width: 10, height: 10, color: '#FFFFFF', flexShrink: 0, opacity: 0.5 }} />
            )}
            <span
              onClick={(e) => { e.stopPropagation(); onAddTask(); }}
              className="flex-shrink-0 rounded transition-colors p-0.5 opacity-0 group-hover:opacity-100"
              style={{ color: '#FFFFFF' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLSpanElement).style.background = '#2A2A2A'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLSpanElement).style.background = 'transparent'; }}
              title="Add task"
            >
              <Plus style={{ width: 11, height: 11 }} />
            </span>
            <span
              className="flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <ProjectFolderThreeDotMenu
                project={project}
                isPinned={isPinned}
                onRename={onRename}
                onDelete={onDelete}
                onTogglePin={onTogglePin}
              />
            </span>
          </>
        )}
      </div>

      {isOpen && (
        <div style={{ paddingLeft: 22 }}>
          {tasks.slice(0, 10).map((task) => {
            const isActiveTask = task.id === activeTaskId;
            return (
              <button
                key={task.id}
                onClick={() => onClickTask(task)}
                className="w-full flex items-center gap-2 text-left transition-colors"
                style={{
                  padding: '5px 6px 5px 8px',
                  borderRadius: '6px',
                  fontSize: '12.5px',
                  color: '#FFFFFF',
                  background: isActiveTask ? '#2A2A2A' : 'transparent',
                  fontWeight: isActiveTask ? 500 : 400,
                  borderLeft: isActiveTask ? '2px solid #FFFFFF' : '2px solid transparent',
                }}
                onMouseEnter={(e) => { if (!isActiveTask) (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A'; }}
                onMouseLeave={(e) => { if (!isActiveTask) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <MessageCircle style={{ width: 11, height: 11, color: '#FFFFFF', flexShrink: 0 }} />
                <span className="truncate flex-1">{task.name}</span>
              </button>
            );
          })}
          {tasks.length > 10 && onShowAllChats && (
            <button
              onClick={onShowAllChats}
              className="w-full flex items-center gap-1.5 transition-colors"
              style={{
                padding: '4px 6px 4px 8px',
                borderRadius: '6px',
                fontSize: '11.5px',
                color: 'rgba(255,255,255,0.45)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.75)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; }}
            >
              <span>All chats ({tasks.length})</span>
              <ChevronRight style={{ width: 10, height: 10, flexShrink: 0 }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TaskThreeDotMenu({
  task,
  isActive,
  rootProjects,
  onRename,
  onDelete,
  onMoveToProject,
  onToggleFavorite,
  isFavorite,
}: {
  task: ApiProject;
  isActive: boolean;
  rootProjects: ApiProject[];
  onRename: () => void;
  onDelete: () => void;
  onMoveToProject: (projectId: number) => void;
  onToggleFavorite: () => void;
  isFavorite: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; flipped: boolean }>({ top: 0, left: 0, flipped: false });
  const [subMenuPos, setSubMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const menuMinWidth = 180;

  const openMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const menuH = 220;
    const flipped = rect.bottom + menuH > viewportH - 20;
    const left = rect.right + 4;
    setMenuPos({ top: flipped ? rect.top - menuH : rect.bottom + 4, left, flipped });
    setMenuOpen(true);
    setSubMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setSubMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setMenuOpen(false); setSubMenuOpen(false); }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  return (
    <>
      <button
        ref={btnRef}
        data-testid={`task-menu-btn-${task.id}`}
        onClick={openMenu}
        className="flex-shrink-0 flex items-center justify-center transition-colors"
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          background: 'transparent',
          color: '#FFFFFF',
          opacity: isActive ? 1 : undefined,
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#2A2A2A'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        title="Task options"
      >
        <MoreHorizontal style={{ width: 13, height: 13 }} />
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          data-testid={`task-menu-dropdown-${task.id}`}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 9999,
            minWidth: menuMinWidth,
            background: '#FFFFFF',
            border: '1px solid #E5E2DC',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            padding: '6px 0',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            icon={<Pencil style={{ width: 13, height: 13 }} />}
            label="Rename"
            testId={`task-menu-rename-${task.id}`}
            onClick={() => { setMenuOpen(false); onRename(); }}
          />
          <MenuItem
            icon={<Star style={{ width: 13, height: 13, fill: isFavorite ? '#F59E0B' : 'none', color: isFavorite ? '#F59E0B' : undefined }} />}
            label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            testId={`task-menu-favorite-${task.id}`}
            onClick={() => { setMenuOpen(false); onToggleFavorite(); }}
          />
          <MenuItem
            icon={<ExternalLink style={{ width: 13, height: 13 }} />}
            label="Open in new tab"
            testId={`task-menu-newtab-${task.id}`}
            onClick={() => { setMenuOpen(false); window.open(`/canvas?task=${task.id}`, '_blank'); }}
          />
          <div
            onMouseEnter={(e) => {
              const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
              setSubMenuPos({ top: rect.top, left: menuPos.left + menuMinWidth + 4 });
              setSubMenuOpen(true);
            }}
            onMouseLeave={() => setSubMenuOpen(false)}
          >
            <MenuItem
              icon={<FolderInput style={{ width: 13, height: 13 }} />}
              label="Move to project"
              testId={`task-menu-move-${task.id}`}
              hasArrow
              onClick={() => setSubMenuOpen((v) => !v)}
            />
            {subMenuOpen && (
              <div
                data-testid={`task-submenu-${task.id}`}
                style={{
                  position: 'fixed',
                  top: subMenuPos.top,
                  left: subMenuPos.left,
                  minWidth: 160,
                  background: '#FFFFFF',
                  border: '1px solid #E5E2DC',
                  borderRadius: 12,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  padding: '6px 0',
                  zIndex: 10000,
                }}
              >
                {rootProjects.length === 0 ? (
                  <div style={{ padding: '6px 14px', fontSize: 12, color: '#B5B0A8' }}>No projects</div>
                ) : (
                  rootProjects.map((proj) => (
                    <button
                      key={proj.id}
                      data-testid={`task-move-project-${proj.id}`}
                      onClick={() => { setMenuOpen(false); setSubMenuOpen(false); onMoveToProject(proj.id); }}
                      className="w-full flex items-center gap-2 text-left"
                      style={{ padding: '6px 14px', fontSize: 12.5, color: '#3D3A36', background: 'transparent' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: proj.color || '#6366F1', flexShrink: 0 }} />
                      <span className="truncate">{proj.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div style={{ margin: '5px 0', borderTop: '1px solid #E5E2DC' }} />

          <MenuItem
            icon={<Trash2 style={{ width: 13, height: 13 }} />}
            label="Delete"
            testId={`task-menu-delete-${task.id}`}
            danger
            onClick={() => { setMenuOpen(false); onDelete(); }}
          />
        </div>
      )}
    </>
  );
}

function ProjectFolderThreeDotMenu({
  project,
  isPinned,
  onRename,
  onDelete,
  onTogglePin,
}: {
  project: ApiProject;
  isPinned: boolean;
  onRename: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const menuW = 180;
    const menuH = 140;
    const flipped = rect.bottom + menuH > viewportH - 20;
    const rightFromViewport = window.innerWidth - rect.right;
    const clampedRight = Math.max(rightFromViewport, Math.max(0, window.innerWidth - rect.left - menuW));
    setMenuPos({ top: flipped ? rect.top - menuH : rect.bottom + 4, right: clampedRight });
    setMenuOpen(true);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  return (
    <>
      <button
        ref={btnRef}
        data-testid={`folder-menu-btn-${project.id}`}
        onClick={openMenu}
        className="flex-shrink-0 flex items-center justify-center transition-colors"
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          background: 'transparent',
          color: '#8C8578',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#D0CECA'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        title="Folder options"
      >
        <MoreHorizontal style={{ width: 13, height: 13 }} />
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          data-testid={`folder-menu-dropdown-${project.id}`}
          style={{
            position: 'fixed',
            top: menuPos.top,
            right: menuPos.right,
            zIndex: 9999,
            minWidth: 180,
            background: '#FFFFFF',
            border: '1px solid #E5E2DC',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            padding: '6px 0',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            icon={isPinned ? <PinOff style={{ width: 13, height: 13 }} /> : <Pin style={{ width: 13, height: 13 }} />}
            label={isPinned ? 'Unpin folder' : 'Pin to top'}
            testId={`folder-menu-pin-${project.id}`}
            onClick={() => { setMenuOpen(false); onTogglePin(); }}
          />
          <MenuItem
            icon={<Pencil style={{ width: 13, height: 13 }} />}
            label="Rename"
            testId={`folder-menu-rename-${project.id}`}
            onClick={() => { setMenuOpen(false); onRename(); }}
          />
          <div style={{ margin: '5px 0', borderTop: '1px solid #E5E2DC' }} />
          <MenuItem
            icon={<Trash2 style={{ width: 13, height: 13 }} />}
            label="Delete"
            testId={`folder-menu-delete-${project.id}`}
            danger
            onClick={() => { setMenuOpen(false); onDelete(); }}
          />
        </div>
      )}
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger = false,
  hasArrow = false,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  hasArrow?: boolean;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-2.5 text-left"
      style={{
        padding: '6px 14px',
        fontSize: 12.5,
        color: danger ? '#DC2626' : '#3D3A36',
        background: 'transparent',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
    >
      <span style={{ color: danger ? '#DC2626' : '#8C8578', flexShrink: 0 }}>{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {hasArrow && <ChevronRight style={{ width: 11, height: 11, color: '#B5B0A8', flexShrink: 0 }} />}
    </button>
  );
}

// v7.09 — collapsed-rail hover label (Dan: "a little bubble pop out to the
// side with the name"). This existed but was INVISIBLE: it was absolutely
// positioned at left:100%+10px inside the 60px rail, and BOTH the rail and
// its collapse-animation wrapper are overflow-hidden, so it was clipped away
// entirely. Now it portals to <body> at fixed coords measured from the
// button, which escapes both clips (and every z-index in the canvas).
function Tooltip({ label, visible, anchor }: { label: string; visible: boolean; anchor: DOMRect | null }) {
  if (!visible || !anchor || typeof document === 'undefined') return null;
  return createPortal(
    <div
      role="tooltip"
      data-testid={`sidebar-tooltip-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        position: 'fixed',
        left: anchor.right + 10,
        top: anchor.top + anchor.height / 2,
        transform: 'translateY(-50%)',
        background: '#1A1A1A',
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 500,
        padding: '5px 9px',
        borderRadius: 6,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
        zIndex: 10000,
      }}
    >
      {label}
    </div>,
    document.body,
  );
}

function CollapsedIconButton({
  icon: Icon,
  label,
  onClick,
  active = false,
  showTooltip = true,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  active?: boolean;
  showTooltip?: boolean;
}) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  // Measured at hover time: the portalled tooltip is position:fixed, so it
  // needs the button's viewport rect (and a fresh one each hover, since the
  // rail scrolls and the sidebar animates).
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (!showTooltip) return;
    tooltipTimerRef.current = setTimeout(() => {
      setAnchorRect(wrapRef.current?.getBoundingClientRect() ?? null);
      setTooltipVisible(true);
    }, 150);
  };

  const handleMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    setTooltipVisible(false);
  };

  // Never leave a tooltip stranded if the button unmounts mid-hover.
  useEffect(() => () => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={onClick}
        data-testid={`collapsed-icon-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className="flex items-center justify-center transition-colors"
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: active ? '#2A2A2A' : 'transparent',
          color: active ? '#FFFFFF' : '#777777',
        }}
        onMouseEnter={(e) => {
          if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A';
        }}
        onMouseLeave={(e) => {
          if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        <Icon style={{ width: 18, height: 18 }} />
      </button>
      {showTooltip && <Tooltip label={label} visible={tooltipVisible} anchor={anchorRect} />}
    </div>
  );
}

function ProjectsHoverBubble({
  projects,
  activeProjectId,
  onClickProject,
  onNewProject,
  onClose,
}: {
  projects: ApiProject[];
  activeProjectId: number | null;
  onClickProject: (p: ApiProject) => void;
  onNewProject: () => void;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        left: 'calc(100% + 8px)',
        top: 0,
        width: 220,
        background: '#FFFFFF',
        border: '1px solid #E8E5E0',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px 8px',
          borderBottom: '1px solid #F0EDE9',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Projects</span>
        <button
          onClick={(e) => { e.stopPropagation(); onNewProject(); }}
          className="flex items-center justify-center transition-colors"
          style={{ width: 22, height: 22, borderRadius: 5, background: 'transparent', color: '#8C8578' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE9')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          title="New project"
        >
          <Plus style={{ width: 12, height: 12 }} />
        </button>
      </div>
      <div style={{ padding: '6px 6px' }}>
        {projects.length === 0 ? (
          <div style={{ padding: '8px 10px', fontSize: 13, color: '#B5B0A8' }}>No projects yet</div>
        ) : (
          projects.map((p) => {
            const isActive = p.id === activeProjectId;
            return (
              <button
                key={p.id}
                onClick={() => { onClickProject(p); onClose(); }}
                className="w-full flex items-center gap-2 text-left transition-colors"
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontSize: 13,
                  color: isActive ? '#1A1A1A' : '#3D3A36',
                  background: isActive ? '#EBEBEB' : 'transparent',
                  fontWeight: isActive ? 500 : 400,
                }}
                onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '#F5F4F2'; }}
                onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || '#6366F1', flexShrink: 0 }} />
                <span className="truncate flex-1">{p.name}</span>
              </button>
            );
          })
        )}
      </div>
      <div style={{ borderTop: '1px solid #F0EDE9', padding: '6px 6px' }}>
        <button
          onClick={() => { onNewProject(); onClose(); }}
          className="w-full flex items-center gap-2 text-left transition-colors"
          style={{ padding: '6px 10px', borderRadius: 8, fontSize: 13, color: '#B5B0A8' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F4F2'; (e.currentTarget as HTMLButtonElement).style.color = '#8C8578'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#B5B0A8'; }}
        >
          <Plus style={{ width: 12, height: 12 }} />
          <span>New project</span>
        </button>
      </div>
    </div>
  );
}

function AllTasksHoverBubble({
  tasks,
  activeTaskId,
  onClickTask,
  onClose,
}: {
  tasks: ApiProject[];
  activeTaskId: number | null;
  onClickTask: (task: ApiProject) => void;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        left: 'calc(100% + 8px)',
        top: 0,
        width: 220,
        background: '#FFFFFF',
        border: '1px solid #E8E5E0',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #F0EDE9' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.06em' }}>All Tasks</span>
      </div>
      <div style={{ padding: '6px 6px', maxHeight: 300, overflowY: 'auto' }}>
        {tasks.length === 0 ? (
          <div style={{ padding: '8px 10px', fontSize: 13, color: '#B5B0A8' }}>No tasks yet</div>
        ) : (
          tasks.map((task) => {
            const isActive = task.id === activeTaskId;
            return (
              <button
                key={task.id}
                onClick={() => { onClickTask(task); onClose(); }}
                className="w-full flex items-center gap-2 text-left transition-colors"
                style={{
                  padding: '5px 8px',
                  borderRadius: 7,
                  fontSize: 12.5,
                  color: isActive ? '#1A1A1A' : '#3D3A36',
                  background: isActive ? '#EBEBEB' : 'transparent',
                  fontWeight: isActive ? 500 : 400,
                  borderLeft: isActive ? '2px solid #1A1A1A' : '2px solid transparent',
                }}
                onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '#F5F4F2'; }}
                onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <MessageCircle style={{ width: 11, height: 11, color: isActive ? '#1A1A1A' : '#8C8578', flexShrink: 0 }} />
                <span className="truncate flex-1">{task.name}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function HoverBubbleWrapper({
  children,
  bubble,
  onClose,
}: {
  children: React.ReactNode;
  bubble: React.ReactNode;
  onClose: () => void;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setIsVisible(true);
  }, []);

  const hide = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 150);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      <div
        style={{
          position: 'absolute',
          left: '100%',
          top: 0,
          paddingLeft: 8,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateX(0)' : 'translateX(-6px)',
          transition: 'opacity 150ms, transform 150ms',
          pointerEvents: isVisible ? 'auto' : 'none',
          zIndex: 9999,
        }}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {bubble}
      </div>
    </div>
  );
}

export function CanvasSidebar() {
  const {
    isSidebarOpen,
    isSidebarCollapsed,
    toggleSidebar,
    toggleSidebarCollapsed,
    folderProjectId,
    openFolderIds,
    switchProject,
    setFolderProjectId,
    toggleOpenFolder,
    addOpenFolder,
    setActiveView,
  } = useCanvasStore();
  const persistenceProjectId = useActiveProjectId();
  const activeView = useActiveProjectActiveView();
  const messages = useActiveProjectMessages();
  const [, navigate] = useLocation();
  const authUser = useAuthUser();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [projectsCollapsed, setProjectsCollapsed] = useState(false);
  const [allTasksCollapsed, setAllTasksCollapsed] = useState(false);
  const [showAllChatsModal, setShowAllChatsModal] = useState(false);
  const [allChatsSearch, setAllChatsSearch] = useState('');
  const [allChatsFolderFilter, setAllChatsFolderFilter] = useState<number | null>(null);
  const [renamingTaskId, setRenamingTaskId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [favoriteTaskIds, setFavoriteTaskIds] = useState<Set<number>>(new Set());
  const renameInputRef = useRef<HTMLInputElement>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<number | null>(null);
  const [folderRenameValue, setFolderRenameValue] = useState('');
  const [pendingDeleteTask, setPendingDeleteTask] = useState<ApiProject | null>(null);
  const [pendingDeleteFolder, setPendingDeleteFolder] = useState<ApiProject | null>(null);
  const folderRenameInputRef = useRef<HTMLInputElement>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  useEffect(() => {
    if (renamingTaskId !== null && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingTaskId]);

  useEffect(() => {
    if (renamingFolderId !== null && folderRenameInputRef.current) {
      folderRenameInputRef.current.focus();
      folderRenameInputRef.current.select();
    }
  }, [renamingFolderId]);

  // v7.50 — which brands are mid-onboarding, so their fresh folders render
  // as in-progress. Shares the Brands grid's query key (one cache, one poll);
  // 3s while something's scanning, otherwise a slow idle tick.
  const { data: scanningBrands = {} } = useQuery<Record<string, string>>({
    queryKey: ['/api/brands/onboarding/active'],
    refetchInterval: (q) => (Object.keys((q.state.data as Record<string, string>) ?? {}).length > 0 ? 3000 : 20000),
  });

  // v7.53 — a failed fetch THROWS so React Query retries with backoff and
  // keeps the last good list. Returning [] on !ok made one transient 500
  // (cold DB pool, a deploy restart) blank the whole folder list — "New
  // project" empty state — until the next refetch window.
  const rootProjects = useQuery<ApiProject[]>({
    queryKey: ['/api/canvas/projects', 'rootOnly'],
    queryFn: async () => {
      const resp = await fetch('/api/canvas/projects?rootOnly=1');
      if (!resp.ok) throw new Error(`projects ${resp.status}`);
      return resp.json();
    },
    staleTime: 30000,
    retry: 3,
    placeholderData: (prev) => prev,
  });

  const allProjectsQuery = useQuery<ApiProject[]>({
    queryKey: ['/api/canvas/projects', 'all'],
    queryFn: async () => {
      const resp = await fetch('/api/canvas/projects');
      if (!resp.ok) throw new Error(`projects ${resp.status}`);
      return resp.json();
    },
    staleTime: 15000,
    retry: 3,
    placeholderData: (prev) => prev,
  });
  const allProjects = allProjectsQuery.data ?? [];
  const allTasks = allProjects.filter((p) => !p.isFolder && !p.is_folder);

  // Sync activeBrandId from the currently-open brand folder. This catches the
  // case where folderProjectId was set in an earlier session/render but
  // activeBrandId was never propagated, so brand-scoped views (Ads Manager,
  // Analytics) see the correct brand without requiring an explicit re-click.
  // The API returns snake_case (brand_id) but the type allows both — read
  // with both keys so the value is found regardless of serialization.
  const activeBrandIdInStore = useCanvasStore((s) => s.activeBrandId);
  const cachedActiveParent = useCanvasStore(s => persistenceProjectId != null ? s.projects[persistenceProjectId]?.parentId : null);
  const activeTask = allProjects.find(p => p.id === persistenceProjectId);
  const expandedProjectId = cachedActiveParent ?? activeTask?.parentId ?? activeTask?.parent_id ?? folderProjectId
    ?? (rootProjects.data ?? []).find(p => activeBrandIdInStore != null && (p.brandId ?? p.brand_id) === activeBrandIdInStore)?.id;
  useEffect(() => {
    // Navigation follows the active project; disclosure clicks remain manual
    // until the task/project changes. Empty New task retains the last expansion.
    if (expandedProjectId == null) return;
    useCanvasStore.setState(s => s.openFolderIds.length === 1 && s.openFolderIds[0] === expandedProjectId
      ? s : { openFolderIds: [expandedProjectId] });
    setProjectsCollapsed(false);
  }, [expandedProjectId, persistenceProjectId]);

  useEffect(() => {
    if (folderProjectId == null) return;
    const folder = allProjects.find((p) => p.id === folderProjectId);
    const activeTask = useCanvasStore.getState().activeProjectId;
    if (activeTask != null && useCanvasStore.getState().projects[activeTask]?.brandId != null) return;
    const folderBrandId = folder?.brandId ?? folder?.brand_id ?? null;
    if (folderBrandId != null && folderBrandId !== activeBrandIdInStore) {
      useCanvasStore.setState({ activeBrandId: folderBrandId });
    }
  }, [folderProjectId, allProjects, activeBrandIdInStore]);

  const handleProjectCreated = (projectId: number) => {
    setShowCreateModal(false);
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
    setFolderProjectId(projectId);
    useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
  };

  // v7.50 (Dan): the folder's "+" must land on the New Task screen with that
  // brand loaded from ANY page. It used to only set the folder context — from
  // Brands/Analytics/etc. the embedded view stayed put and nothing visibly
  // happened. Same view reset handleNewTask does, plus the folder + brand.
  const handleAddTaskToFolder = (folder: ApiProject) => {
    addOpenFolder(folder.id);
    setFolderProjectId(folder.id);
    useCanvasStore.setState({
      activeProjectId: null,
      activeTaskId: null,
      activeBrandId: folder.brandId ?? folder.brand_id ?? null,
    });
    setActiveView('chat-only');
    if (!WORKSPACE_ROUTE_PATTERN.test(window.location.pathname)) {
      navigate('/canvas');
    }
  };

  const handleClickFolder = (p: ApiProject) => {
    // Folder disclosure must not change the open task, brand, or page.
    // The folder's + button is the explicit action for starting a task there.
    toggleOpenFolder(p.id);
  };

  const handleClickTask = async (task: ApiProject) => {
    if (task.id === persistenceProjectId) return;
    const existing = useCanvasStore.getState().projects[task.id];
    const parentId = task.parentId ?? task.parent_id ?? existing?.parentId ?? null;
    const metadata = resolveProjectNavigation(task, existing, allProjects.find(p => p.id === parentId));
    if (metadata.parentId) {
      addOpenFolder(metadata.parentId);
      setFolderProjectId(metadata.parentId);
    } else {
      setFolderProjectId(null);
    }
    await switchProject(task.id, metadata);
    // v7.27 — a task click always lands on the TASK (chat/canvas), never a
    // browse view. Two ways a browse view could show instead: the global
    // standalone view was Ad Library/Brands/etc. when the click happened, or
    // a browse view got BAKED INTO this task's own per-project slot (the
    // pre-v7.27 view-chip bug — Dan's dinosaur task reopened on Ad Templates
    // every time). setActiveView here writes to the now-active task's slot,
    // which also permanently repairs a corrupted one.
    const st = useCanvasStore.getState();
    if (st.activeProjectId !== task.id) return;
    navigate(workspaceUrl('chat-only', task.id));
    const storedView = st.projects[task.id]?.activeView;
    if (storedView !== 'chat-only' && storedView !== 'chat-canvas') {
      const hasCanvas = (st.projects[task.id]?.images?.length ?? 0) > 0 || st.projects[task.id]?.isCanvasOpen === true;
      setActiveView(hasCanvas ? 'chat-canvas' : 'chat-only');
    }
  };

  const handleNewTask = () => {
    const isOnEmptyWelcome = !persistenceProjectId && activeView === 'chat-only' && messages.length === 0;
    if (isOnEmptyWelcome && WORKSPACE_ROUTE_PATTERN.test(window.location.pathname)) return;
    setFolderProjectId(null);
    useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
    // Switch back to the canvas/chat view so a click from Brands/Analytics
    // actually lands on the empty-task welcome screen instead of leaving
    // the user staring at the same embedded view.
    setActiveView('chat-only');
    // When the sidebar is used from a non-canvas page (e.g. /mock-data) the
    // chat-only view only renders under /canvas — make sure we route there.
    if (!WORKSPACE_ROUTE_PATTERN.test(window.location.pathname)) {
      navigate('/canvas');
    }
  };

  const handleRenameStart = (task: ApiProject) => {
    setRenamingTaskId(task.id);
    setRenameValue(task.name);
  };

  const handleRenameSave = async (taskId: number) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingTaskId(null);
      return;
    }
    setRenamingTaskId(null);
    await fetch(`/api/canvas/tasks/${taskId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: trimmed }),
    });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
    if (folderProjectId) {
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', folderProjectId, 'tasks'] });
    }
  };

  const handleRenameCancel = () => {
    setRenamingTaskId(null);
    setRenameValue('');
  };

  const handleDeleteTask = (task: ApiProject) => {
    setPendingDeleteTask(task);
  };

  const confirmDeleteTask = async () => {
    if (!pendingDeleteTask) return;
    const task = pendingDeleteTask;
    setPendingDeleteTask(null);
    const wasActive = task.id === persistenceProjectId;
    await fetch(`/api/canvas/tasks/${task.id}`, { method: 'DELETE' });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
    if (task.parentId) {
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', task.parentId, 'tasks'] });
    }
    if (wasActive) {
      useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
    }
  };

  const handleMoveToProject = async (task: ApiProject, projectId: number | null) => {
    const resp = await fetch(`/api/canvas/tasks/${task.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId: projectId }),
    });
    const updatedTask = resp.ok ? await resp.json() : null;
    if (updatedTask) {
      // A successful move is authoritative, including intentional nulls when
      // moving to the root or a folder with no linked account.
      const metadata = resolveProjectNavigation(updatedTask);
      useCanvasStore.getState()._writeProjectMetadata(task.id, metadata);
      queryClient.setQueriesData({ queryKey: ['/api/canvas/projects'] }, (cached: unknown) => {
        if (Array.isArray(cached)) return cached.map(row => row?.id === task.id ? updatedTask : row);
        return cached && typeof cached === 'object' && 'id' in cached && cached.id === task.id ? updatedTask : cached;
      });
    }
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
    if (task.parentId) {
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', task.parentId, 'tasks'] });
    }
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', projectId, 'tasks'] });
    }
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', task.id] });
    if (updatedTask && useCanvasStore.getState().activeProjectId === task.id) {
      const metadata = resolveProjectNavigation(updatedTask);
      useCanvasStore.setState({
        activeBrandId: metadata.brandId,
        activeAdAccountId: metadata.adAccountId,
        folderProjectId: projectId,
      });
    }
  };

  const handleToggleFavorite = (taskId: number) => {
    setFavoriteTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const handleFolderRenameStart = (p: ApiProject) => {
    setRenamingFolderId(p.id);
    setFolderRenameValue(p.name);
  };

  const handleFolderRenameSave = async (folderId: number) => {
    const trimmed = folderRenameValue.trim();
    setRenamingFolderId(null);
    if (!trimmed) return;
    await fetch(`/api/canvas/projects/${folderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: trimmed }),
    });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
  };

  const handleFolderRenameCancel = () => {
    setRenamingFolderId(null);
    setFolderRenameValue('');
  };

  const handleDeleteFolder = (p: ApiProject) => {
    setPendingDeleteFolder(p);
  };

  const confirmDeleteFolder = async () => {
    if (!pendingDeleteFolder) return;
    const p = pendingDeleteFolder;
    setPendingDeleteFolder(null);
    const wasActiveFolder = p.id === folderProjectId;
    await fetch(`/api/canvas/projects/${p.id}`, { method: 'DELETE' });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
    if (wasActiveFolder) {
      setFolderProjectId(null);
      useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
    }
  };

  const handleToggleFolderPin = async (p: ApiProject) => {
    const currentPinned = !!(p.isPinned || p.is_pinned);
    await fetch(`/api/canvas/projects/${p.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isPinned: !currentPinned }),
    });
    queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
  };

  const handleComingSoon = (label: string) => {
    toast({ title: `${label} coming soon`, description: 'This feature is not yet available.' });
  };

  const rootProjectsList = rootProjects.data ?? [];

  return (
    <>
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleProjectCreated}
        />
      )}
      {showSettingsModal && (
        <SettingsModal onClose={() => setShowSettingsModal(false)} />
      )}

      <AlertDialog open={!!pendingDeleteTask} onOpenChange={(open) => { if (!open) setPendingDeleteTask(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDeleteTask?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-task-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-task-confirm"
              onClick={confirmDeleteTask}
              className="bg-red-500 hover:bg-red-600 text-white border-0"
              style={{ backgroundColor: '#EF4444', color: '#fff' }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDeleteFolder} onOpenChange={(open) => { if (!open) setPendingDeleteFolder(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDeleteFolder?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-folder-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-folder-confirm"
              onClick={confirmDeleteFolder}
              className="bg-red-500 hover:bg-red-600 text-white border-0"
              style={{ backgroundColor: '#EF4444', color: '#fff' }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className="flex-shrink-0 flex flex-col h-full overflow-hidden transition-all duration-[200ms] ease-in-out"
        style={{
          width: isSidebarOpen ? (isSidebarCollapsed ? 60 : 270) : 0,
          opacity: isSidebarOpen ? 1 : 0,
        }}
      >
        {/* COLLAPSED SIDEBAR */}
        {isSidebarCollapsed && (
          <div
            className="flex flex-col h-full overflow-hidden"
            style={{ width: 60, background: '#000000', borderRight: '1px solid #1A1A1A', alignItems: 'center' }}
          >
            {/* Toggle button */}
            <div style={{ paddingTop: 14, paddingBottom: 10, borderBottom: '1px solid #1A1A1A', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <button
                data-testid="canvas-sidebar-toggle-collapsed"
                onClick={toggleSidebarCollapsed}
                className="flex items-center justify-center transition-colors"
                style={{ width: 46, height: 36, borderRadius: 8, background: 'transparent', color: '#FFFFFF' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                title="Expand sidebar"
              >
                <img src="/admoji-logo-new.jpg" alt="Admoji" style={{ width: 22, height: 22, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                <ChevronRight style={{ width: 14, height: 14, marginLeft: 2 }} />
              </button>
            </div>

            {/* Main icon strip */}
            <div className="flex flex-col items-center flex-1" style={{ paddingTop: 8, gap: 2, width: '100%' }}>
              {/* New task */}
              <CollapsedIconButton icon={PenSquare} label="New task" onClick={handleNewTask} />

              {/* v3.92 — collapsed rail order mirrors the expanded TOP_NAV:
                  Discover, Brands, Analytics, Heartbeat. */}
              {/* Discover (was Ad library) */}
              <CollapsedIconButton
                icon={LayoutGrid}
                label="Discover"
                active={activeView === 'ad-library'}
                onClick={() => {
                  useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                  setActiveView('ad-library');
                  navigate(workspaceUrl('ad-library'));
                }}
              />

              {/* Assets */}
              <CollapsedIconButton
                icon={FolderOpen}
                label="Assets"
                active={activeView === 'assets'}
                onClick={() => {
                  useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                  setActiveView('assets');
                  navigate(workspaceUrl('assets'));
                }}
              />

              {/* FUTURE-EDITOR (v6.66): Projects entry parked per Dan.
              <CollapsedIconButton
                icon={Clapperboard}
                label="Projects"
                active={false}
                onClick={() => { window.location.href = '/api/video-editor/projects/'; }}
              />
              */}

              {/* Brands */}
              <CollapsedIconButton
                icon={Tag}
                label="Brands"
                active={activeView === 'brands'}
                onClick={() => {
                  useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                  setActiveView('brands');
                  navigate(workspaceUrl('brands'));
                }}
              />

              {/* Analytics */}
              <CollapsedIconButton icon={BarChart3} label="Analytics" active={activeView === 'analytics'} onClick={() => {
                useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                setActiveView('analytics');
                navigate(workspaceUrl('analytics'));
              }} />

              {/* Heartbeat (was Heartbeat AI) */}
              <CollapsedIconButton
                icon={Activity}
                label="Heartbeat"
                active={activeView === 'heartbeat'}
                onClick={() => {
                  useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                  setActiveView('heartbeat');
                  navigate(workspaceUrl('heartbeat'));
                }}
              />

              {/* Ads Manager — admin-only until user-ready (mirrors Dev Tools) */}
              {authUser?.isAdmin && (
                <CollapsedIconButton
                  icon={Megaphone}
                  label="Ads Manager"
                  active={activeView === 'ads-manager'}
                  onClick={() => {
                    useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                    setActiveView('ads-manager');
                    navigate(workspaceUrl('ads-manager'));
                  }}
                />
              )}

              {/* Divider */}
              <div style={{ width: 32, borderTop: '1px solid #1A1A1A', margin: '4px 0' }} />

              {/* Projects bubble */}
              <HoverBubbleWrapper
                bubble={
                  <ProjectsHoverBubble
                    projects={rootProjectsList}
                    activeProjectId={folderProjectId}
                    onClickProject={handleClickFolder}
                    onNewProject={() => setShowCreateModal(true)}
                    onClose={() => {}}
                  />
                }
                onClose={() => {}}
              >
                <div style={{ position: 'relative' }}>
                  <button
                    data-testid="collapsed-icon-projects"
                    // v7.09 (Dan) — this had NO onClick: clicking the brand
                    // folder in the collapsed rail did nothing, and its hover
                    // bubble is clipped by the rail's overflow-hidden. Click
                    // now expands the sidebar so the folder's contents are
                    // actually visible.
                    onClick={toggleSidebarCollapsed}
                    title="Open brands"
                    className="flex items-center justify-center transition-colors"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: folderProjectId ? '#2A2A2A' : 'transparent',
                      color: folderProjectId ? '#FFFFFF' : '#777777',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!folderProjectId) (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A';
                    }}
                    onMouseLeave={(e) => {
                      if (!folderProjectId) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <Folder style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </HoverBubbleWrapper>

              {/* All Tasks bubble — hidden while tasks are brand-scoped (v6.62) */}
              {SHOW_ALL_TASKS && (
              <HoverBubbleWrapper
                bubble={
                  <AllTasksHoverBubble
                    tasks={allTasks}
                    activeTaskId={persistenceProjectId}
                    onClickTask={handleClickTask}
                    onClose={() => {}}
                  />
                }
                onClose={() => {}}
              >
                <div style={{ position: 'relative' }}>
                  <button
                    data-testid="collapsed-icon-all-tasks"
                    className="flex items-center justify-center transition-colors"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: persistenceProjectId ? '#2A2A2A' : 'transparent',
                      color: persistenceProjectId ? '#FFFFFF' : '#777777',
                    }}
                    onMouseEnter={(e) => {
                      if (!persistenceProjectId) (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A';
                    }}
                    onMouseLeave={(e) => {
                      if (!persistenceProjectId) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <ListTodo style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </HoverBubbleWrapper>
              )}
            </div>

            {/* Bottom icons */}
            <div
              className="flex flex-col items-center"
              style={{ paddingBottom: 12, gap: 2, borderTop: '1px solid #1A1A1A', paddingTop: 8, width: '100%' }}
            >
              {BOTTOM_ICONS.map((item) => (
                <CollapsedIconButton
                  key={item.label}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => {
                    if (item.label === 'Settings') {
                      setShowSettingsModal(true);
                    } else {
                      handleComingSoon(item.label);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* EXPANDED SIDEBAR */}
        {!isSidebarCollapsed && (
          <div
            data-testid="sidebar-scroll"
            className="flex flex-col h-full overflow-y-auto overflow-x-hidden"
            style={{ width: 270, background: '#000000', borderRight: '1px solid #1A1A1A' }}
          >
            {/* Logo row */}
            <div
              className="flex items-center flex-shrink-0"
              style={{ padding: '14px 16px 12px', borderBottom: '1px solid #1A1A1A' }}
            >
              <div className="flex flex-col flex-1 min-w-0">
                <img src="/admoji-logo-white.png" style={{ height: 28, width: 'auto', objectFit: 'contain' }} alt="Admoji" />
              </div>
              <button
                data-testid="canvas-sidebar-toggle"
                onClick={toggleSidebarCollapsed}
                className="flex items-center justify-center transition-colors flex-shrink-0"
                style={{ width: 28, height: 28, borderRadius: 6, background: 'transparent', color: '#FFFFFF' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                title="Collapse sidebar"
              >
                <PanelLeftClose style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Quick actions */}
            <div className="flex-shrink-0" style={{ padding: '10px 8px 4px' }}>
              <NavItem
                icon={Plus}
                label="New task"
                onClick={handleNewTask}
                testId="nav-new-task"
              />
              {TOP_NAV.map((item) => {
                const Icon = item.icon;
                const isHeartbeat = 'view' in item && item.view === 'heartbeat';
                const isBrands = 'view' in item && item.view === 'brands';
                const isAnalytics = 'view' in item && item.view === 'analytics';
                const isAdLibrary = 'view' in item && item.view === 'ad-library';
                const isAssets = 'view' in item && item.view === 'assets';
                const isActive = (isHeartbeat && activeView === 'heartbeat') || (isBrands && activeView === 'brands') || (isAnalytics && activeView === 'analytics') || (isAdLibrary && activeView === 'ad-library') || (isAssets && activeView === 'assets');
                return (
                  <NavItem
                    key={item.label}
                    icon={Icon}
                    label={item.label}
                    active={isActive}
                    testId={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    onClick={() => {
                      // CLEAR activeProjectId BEFORE setActiveView so the
                      // setActiveView call routes to the standalone-view slot.
                      // Otherwise setActiveView writes to the previous project's
                      // per-project slot and the menu click silently no-ops.
                      const standaloneView =
                        isHeartbeat ? 'heartbeat'
                        : isBrands ? 'brands'
                        : isAnalytics ? 'analytics'
                        : isAdLibrary ? 'ad-library'
                        : isAssets ? 'assets'
                        : null;
                      if (standaloneView) {
                        useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                        setActiveView(standaloneView);
                        // Keep section navigation shareable, including from
                        // pages outside the shared workspace.
                        navigate(workspaceUrl(standaloneView));
                      } else if (item.href) {
                        // The video editor is a different app on the same
                        // origin — wouter can't route into it.
                        if (item.href.startsWith('/api/')) window.location.href = item.href;
                        else navigate(item.href);
                      }
                    }}
                  />
                );
              })}
            </div>

            {/* Divider */}
            <div className="flex-shrink-0" style={{ margin: '8px 14px', borderTop: '1px solid #1A1A1A' }} />

            {/* Middle zone: Projects + All Tasks */}
            <div>

            {/* Projects section — v7.37 (Dan): the folder list is "Projects";
                "Brands" stays the name of the Brands page above it. */}
            <div style={{ padding: '4px 8px' }}>
              <SectionHeader
                label="Projects"
                collapsed={projectsCollapsed}
                onToggle={() => setProjectsCollapsed((v) => !v)}
                action={
                  <button
                    data-testid="brands-section-add"
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center justify-center transition-colors"
                    style={{ width: 22, height: 22, borderRadius: 4, background: 'transparent', color: '#FFFFFF' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                    title="New project"
                  >
                    <Plus style={{ width: 12, height: 12 }} />
                  </button>
                }
              />
              <div
                style={{
                  maxHeight: projectsCollapsed ? 0 : 9999,
                  opacity: projectsCollapsed ? 0 : 1,
                  overflow: 'hidden',
                  transition: 'max-height 350ms ease, opacity 350ms ease',
                }}
              >
                {rootProjectsList.length === 0 ? (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="w-full flex items-center gap-2 transition-colors"
                    style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, color: '#FFFFFF' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                  >
                    <Plus style={{ width: 12, height: 12 }} />
                    New project
                  </button>
                ) : (
                  rootProjectsList.map((p) => {
                    const isOpen = openFolderIds.includes(p.id);
                    const folderHasActiveTask = isOpen && !!persistenceProjectId && folderProjectId === p.id;
                    const isFolderRenaming = renamingFolderId === p.id;
                    return (
                      <ProjectRow
                        key={p.id}
                        project={p}
                        isOpen={isOpen}
                        isActiveFolder={folderProjectId === p.id && !persistenceProjectId}
                        hasFolderActiveTask={folderHasActiveTask}
                        activeTaskId={persistenceProjectId}
                        onClickFolder={() => handleClickFolder(p)}
                        onClickTask={handleClickTask}
                        onAddTask={() => handleAddTaskToFolder(p)}
                        analyzing={!!scanningBrands[String(p.brandId ?? p.brand_id ?? '')]}
                        onRename={() => handleFolderRenameStart(p)}
                        onDelete={() => handleDeleteFolder(p)}
                        onTogglePin={() => handleToggleFolderPin(p)}
                        isRenaming={isFolderRenaming}
                        renameValue={folderRenameValue}
                        onRenameChange={setFolderRenameValue}
                        onRenameSave={() => handleFolderRenameSave(p.id)}
                        onRenameCancel={handleFolderRenameCancel}
                        renameInputRef={folderRenameInputRef}
                        onShowAllChats={() => { setAllChatsFolderFilter(p.id); setAllChatsSearch(''); setShowAllChatsModal(true); }}
                      />
                    );
                  })
                )}
              </div>
            </div>

            {/* Divider + All tasks section — hidden while tasks are brand-scoped (v6.62) */}
            {SHOW_ALL_TASKS && (
            <div style={{ margin: '8px 14px', borderTop: '1px solid #DCDCDC' }} />
            )}
            {SHOW_ALL_TASKS && allTasks.length > 0 && (
              <>
                <div className="flex-shrink-0" style={{ padding: '4px 8px' }}>
                  <SectionHeader
                    label="All tasks"
                    collapsed={allTasksCollapsed}
                    onToggle={() => setAllTasksCollapsed((v) => !v)}
                  />
                  <div
                    style={{
                      maxHeight: allTasksCollapsed ? 0 : 9999,
                      opacity: allTasksCollapsed ? 0 : 1,
                      overflow: 'hidden',
                      transition: 'max-height 350ms ease, opacity 350ms ease',
                    }}
                  >
                    {allTasks.slice(0, 10).map((task) => {
                      const isActiveTask = task.id === persistenceProjectId;
                      const isRenaming = renamingTaskId === task.id;
                      const isFav = favoriteTaskIds.has(task.id);
                      return (
                        <div
                          key={task.id}
                          className="group flex items-center gap-1.5"
                          style={{
                            padding: '5px 6px 5px 10px',
                            borderRadius: '7px',
                            fontSize: '12.5px',
                            color: '#FFFFFF',
                            background: isActiveTask ? '#2A2A2A' : 'transparent',
                            fontWeight: isActiveTask ? 500 : 400,
                            borderLeft: isActiveTask ? '2px solid #FFFFFF' : '2px solid transparent',
                            cursor: isRenaming ? 'default' : 'pointer',
                            position: 'relative',
                          }}
                          onClick={() => { if (!isRenaming) handleClickTask(task); }}
                          onMouseEnter={(e) => {
                            if (!isActiveTask && !isRenaming) (e.currentTarget as HTMLDivElement).style.background = '#1A1A1A';
                          }}
                          onMouseLeave={(e) => {
                            if (!isActiveTask) (e.currentTarget as HTMLDivElement).style.background = isActiveTask ? '#2A2A2A' : 'transparent';
                          }}
                          data-testid={`canvas-project-${task.id}`}
                        >
                          <MessageCircle style={{ width: 11, height: 11, color: '#FFFFFF', flexShrink: 0 }} />
                          {isRenaming ? (
                            <input
                              ref={renameInputRef}
                              data-testid={`task-rename-input-${task.id}`}
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleRenameSave(task.id); }
                                if (e.key === 'Escape') { e.preventDefault(); handleRenameCancel(); }
                              }}
                              onBlur={() => handleRenameCancel()}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0"
                              style={{
                                background: 'white',
                                border: '1px solid #E5E2DC',
                                borderRadius: 4,
                                padding: '1px 4px',
                                fontSize: 12.5,
                                color: '#1A1A1A',
                                outline: 'none',
                              }}
                            />
                          ) : (
                            <span className="truncate flex-1">{task.name}</span>
                          )}
                          {isFav && !isRenaming && (
                            <Star style={{ width: 10, height: 10, fill: '#F59E0B', color: '#F59E0B', flexShrink: 0 }} />
                          )}
                          {!isRenaming && (
                            <span
                              className={isActiveTask ? '' : 'opacity-0 group-hover:opacity-100'}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <TaskThreeDotMenu
                                task={task}
                                isActive={isActiveTask}
                                rootProjects={rootProjectsList}
                                onRename={() => handleRenameStart(task)}
                                onDelete={() => handleDeleteTask(task)}
                                onMoveToProject={(projectId) => handleMoveToProject(task, projectId)}
                                onToggleFavorite={() => handleToggleFavorite(task.id)}
                                isFavorite={isFav}
                              />
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {allTasks.length > 10 && (
                      <button
                        data-testid="sidebar-all-chats-link"
                        onClick={() => { setAllChatsFolderFilter(null); setAllChatsSearch(''); setShowAllChatsModal(true); }}
                        className="w-full flex items-center gap-1.5 transition-colors"
                        style={{
                          padding: '4px 10px',
                          borderRadius: '7px',
                          fontSize: '11.5px',
                          color: 'rgba(255,255,255,0.45)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.75)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; }}
                      >
                        <span>All chats ({allTasks.length})</span>
                        <ChevronRight style={{ width: 10, height: 10, flexShrink: 0 }} />
                      </button>
                    )}
                  </div>
                </div>
                {authUser?.isAdmin && (
                  <div style={{ margin: '8px 14px', borderTop: '1px solid #DCDCDC' }} />
                )}
              </>
            )}

            </div>
            {/* End middle zone */}

            {/* Dev Tools (admin only) */}
            {authUser?.isAdmin && (
              <div style={{ padding: '4px 8px' }}>
                <SectionHeader label="Dev Tools" />
                {/* v7.44 — run the first-run tour on demand. Goes to New Task
                    (every pin lives there) and raises a FORCED request, which
                    never stamps tour_completed_at — so it can be replayed. */}
                <NavItem
                  icon={Compass}
                  label="Start tour"
                  testId="devtools-start-tour"
                  onClick={() => {
                    handleNewTask();
                    useCanvasStore.setState({ tourRequest: 'forced' });
                  }}
                />
                {DEV_TOOLS.map((t) => {
                  const Icon = t.icon;
                  const isActive = t.href === null && activeView === t.view;
                  return (
                    <NavItem
                      key={t.label}
                      icon={Icon}
                      label={t.label}
                      active={isActive}
                      onClick={() => {
                        if (t.href === null && t.view) {
                          useCanvasStore.setState({ activeProjectId: null, activeTaskId: null });
                          setActiveView(t.view);
                          navigate(workspaceUrl(t.view));
                        } else if (t.href) {
                          navigate(t.href);
                        }
                      }}
                    />
                  );
                })}
              </div>
            )}

            {/* Bottom icon row */}
            {/* borderTop is gated on isAdmin: for admin users it separates
                the Dev Tools section above from the bottom icons. For
                non-admin users Dev Tools is hidden, so an unconditional
                divider here looks like an orphan line floating above the
                Settings/Docs icons (see fix in c6c17c2 for the matching
                divider above Dev Tools). */}
            <div
              style={{ borderTop: authUser?.isAdmin ? '1px solid #DCDCDC' : 'none', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <button
                data-testid="sidebar-bottom-settings"
                title="Settings"
                onClick={() => setShowSettingsModal(true)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 7,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8C8578',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#DCDCDC'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <Settings style={{ width: 15, height: 15 }} />
              </button>
              <TourHelpButton maxBrands={authUser?.entitlements?.maxBrands} />
              {/* Build version label — bump APP_VERSION in client/src/lib/version.ts
                  on each push so you can confirm new code is live on production.
                  marginLeft auto pushes it to the right edge of the icon row. */}
              <span
                data-testid="app-version"
                title={`Build v${APP_VERSION}`}
                style={{
                  marginLeft: 'auto',
                  fontSize: 10,
                  color: '#8C8578',
                  fontFamily: 'monospace',
                  userSelect: 'none',
                  letterSpacing: '0.02em',
                }}
              >
                v{APP_VERSION}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* All chats full-page panel */}
      {showAllChatsModal && (() => {
        const filterFolder = allChatsFolderFilter != null
          ? rootProjectsList.find((f) => f.id === allChatsFolderFilter) ?? null
          : null;
        const panelTasks = allChatsFolderFilter != null
          ? allTasks.filter((t) => t.parentId === allChatsFolderFilter)
          : allTasks;
        const filteredTasks = panelTasks.filter(
          (t) => !allChatsSearch || t.name.toLowerCase().includes(allChatsSearch.toLowerCase())
        );
        return (
          <div
            data-testid="all-chats-modal-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
            }}
            onClick={() => setShowAllChatsModal(false)}
          >
            <div
              data-testid="all-chats-modal"
              style={{
                background: '#FFFFFF',
                width: '100%',
                maxWidth: 720,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                margin: '40px 20px',
                borderRadius: 14,
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Panel header */}
              <div style={{
                padding: '20px 24px 16px',
                borderBottom: '1px solid #F0EDE8',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0D0D0D', lineHeight: 1.2 }}>
                    {filterFolder ? filterFolder.name : 'Chats'}
                  </div>
                  {filterFolder && (
                    <button
                      onClick={() => setAllChatsFolderFilter(null)}
                      style={{
                        fontSize: 11.5,
                        color: '#8C8578',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px 0 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#3D3A36'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#8C8578'; }}
                    >
                      ← All brands
                    </button>
                  )}
                </div>
                <button
                  data-testid="all-chats-modal-close"
                  onClick={() => setShowAllChatsModal(false)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8C8578',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F4F1EC'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                >
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </div>

              {/* Search */}
              <div style={{ padding: '14px 24px', borderBottom: '1px solid #F0EDE8', flexShrink: 0 }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Search style={{ width: 14, height: 14, color: '#B5B0A8', position: 'absolute', left: 12, pointerEvents: 'none' }} />
                  <input
                    data-testid="all-chats-search-input"
                    autoFocus
                    placeholder="Search chats..."
                    value={allChatsSearch}
                    onChange={(e) => setAllChatsSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setShowAllChatsModal(false); }}
                    style={{
                      width: '100%',
                      padding: '9px 12px 9px 36px',
                      borderRadius: 9,
                      border: '1.5px solid #E5E2DC',
                      background: '#FAFAF9',
                      fontSize: 13.5,
                      color: '#1A1A1A',
                      outline: 'none',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#6366F1'; e.currentTarget.style.background = '#FFFFFF'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E2DC'; e.currentTarget.style.background = '#FAFAF9'; }}
                  />
                </div>
              </div>

              {/* Chat list */}
              <div style={{ overflowY: 'auto', flex: 1, padding: '8px 12px' }}>
                {filteredTasks.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#B5B0A8', fontSize: 13.5 }}>
                    No chats found
                  </div>
                ) : (
                  filteredTasks.map((task, idx) => {
                    const isActiveTask = task.id === persistenceProjectId;
                    const brandFolder = rootProjectsList.find((f) => f.id === task.parentId);
                    return (
                      <div
                        key={task.id}
                        data-testid={`all-chats-task-${task.id}`}
                        style={{
                          borderBottom: idx < filteredTasks.length - 1 ? '1px solid #F4F1EC' : 'none',
                        }}
                      >
                        <div
                          className="flex items-start gap-3"
                          style={{
                            padding: '11px 12px',
                            borderRadius: 9,
                            cursor: 'pointer',
                            background: isActiveTask ? '#F4F1EC' : 'transparent',
                          }}
                          onClick={() => { handleClickTask(task); setShowAllChatsModal(false); }}
                          onMouseEnter={(e) => {
                            if (!isActiveTask) (e.currentTarget as HTMLDivElement).style.background = '#FAFAF9';
                          }}
                          onMouseLeave={(e) => {
                            if (!isActiveTask) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                          }}
                        >
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              background: isActiveTask ? '#1A1A1A' : '#F0EDE8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              marginTop: 1,
                            }}
                          >
                            <MessageCircle style={{ width: 13, height: 13, color: isActiveTask ? '#FFFFFF' : '#8C8578' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13.5,
                                fontWeight: isActiveTask ? 600 : 500,
                                color: '#0D0D0D',
                                lineHeight: 1.35,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {task.name}
                            </div>
                            {brandFolder && (
                              <div style={{ fontSize: 11.5, color: '#B5B0A8', marginTop: 2 }}>
                                {brandFolder.name}
                              </div>
                            )}
                          </div>
                          {isActiveTask && (
                            <div style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: '#6366F1',
                              flexShrink: 0,
                              marginTop: 12,
                            }} />
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer count */}
              <div style={{
                padding: '10px 24px',
                borderTop: '1px solid #F0EDE8',
                fontSize: 11.5,
                color: '#B5B0A8',
                flexShrink: 0,
              }}>
                {filteredTasks.length} chat{filteredTasks.length !== 1 ? 's' : ''}{allChatsSearch ? ' found' : ''}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
